import React, { useEffect, useRef, useState } from "react";
import "../../css/Dashboard.scss";
import "../../css/Common.scss";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import {
  pencilIcon,
  plusIcon,
  searchIcon,
  xIcon,
} from "@progress/kendo-svg-icons";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Breadcrumbs from "../../components/Breadcrumbs";
import { TextBox } from "@progress/kendo-react-inputs";
import {
  commonBaseUrl,
  RightAlignedHeaderCell,
} from "../../../services/CommonUtil";
import { Label } from "@progress/kendo-react-labels";
import { Dialog } from "@progress/kendo-react-dialogs";
import { process, State } from "@progress/kendo-data-query";

const initialDataState: State = {
  take: 5,
  skip: 0,
  group: [],
};

export const BusinessToBusinessMasterList = () => {
  // Set initial state for the dynamic data
  const baseURL = commonBaseUrl();

  const navigate = useNavigate();
  const hasFetched = useRef(false);

  const [originalData, setOriginalData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filterValue, setFilterValue] = useState("");
  const [dataState, setDataState] = useState<State>(initialDataState);

  // Breadcrums
  const breadCrumbsData = [
    {
      id: "1",
      text: "Masters",
      route: "",
    },
    {
      id: "2",
      text: "B2B master",
      route: "",
    },
  ];

  // Pagination with settings
  const handlePageChange = (pageState: any) => {
    setDataState({ ...dataState, ...pageState });
  };

  const getPageableSettings = () => {
    const length = filteredData.length;
    if (length === 0) {
      return false;
    } else if (length <= 5) {
      return { pageSizes: false, buttonCount: 5 };
    } else if (length <= 10) {
      return { pageSizes: [5, 10], buttonCount: 5 };
    } else if (length <= 20) {
      return { pageSizes: [5, 10, 20], buttonCount: 5 };
    } else {
      return { pageSizes: [5, 10, 20, 50], buttonCount: 5 };
    }
  };

  const enterView = (item: any) => {
    navigate("/masters/b2b/createb2b", {
      state: { infoId: item.Id },
    });
  };

  useEffect(() => {
    // Prevent second call
    if (hasFetched.current) return;
    hasFetched.current = true;
    getB2BApi();
  }, []);

  const getB2BApi = async () => {
    try {
      const response = await axios.get(`${baseURL}odata/B2BMasterServices`);
      const b2b = response.data.value;
      setOriginalData(b2b);
      setFilteredData(b2b); // Also initialize searchData with original data
    } catch (err) {
      console.error(err);
    }
  };

  const onFilterChange = (ev: any) => {
    const value = ev.target.value; // Update to use `target.value` from event
    setFilterValue(value);

    if (value === "") {
      setFilteredData(originalData); // Reset to original data when search is empty
    } else {
      const newData = originalData.filter((item: any) => {
        return (
          item.B2BServiceName?.toLowerCase().includes(value.toLowerCase()) ||
          item.B2BServiceDescription?.toLowerCase().includes(
            value.toLowerCase()
          )
        );
      });
      setDataState(initialDataState);
      setFilteredData(newData);
    }
  };

  // Reset filter values
  const resetSearch = () => {
    setFilterValue("");
    setFilteredData(originalData); // Reset to original data
  };

  const MultilineTextCell = (props: any) => {
    const { dataItem, field } = props;
    return (
      <td style={{ whiteSpace: "pre-wrap", wordWrap: "break-word" }}>
        {dataItem[field]}
      </td>
    );
  };

  const handleNavigate = () => {
    navigate("/masters/b2b/createb2b", {
      state: null,
    });
  };

  return (
    <div className="overall-view ">
      <div className="space-burger">
        <Breadcrumbs breadCrumbData={breadCrumbsData} />
        <Button
          className="large-button-view"
          onClick={() => handleNavigate()}
          svgIcon={plusIcon}
        >
          Create B2B
        </Button>
      </div>
      <hr className="hr-border" />
      <div>
        <div className="filter-container"></div>
        <div className="common-margin view-margin">
          <div className="table-title-view">
            <div className="large-custom-input-container">
              <Label className="list-heading">B2B List</Label>
              <TextBox
                className="textBoxcustom"
                placeholder="Search using B2B Service Name & B2B Description"
                name="roomcost"
                value={filterValue}
                onChange={onFilterChange}
                suffix={() => (
                  <>
                    <span className="input-icon">
                      {filterValue ? (
                        <Button svgIcon={xIcon} onClick={resetSearch} />
                      ) : (
                        <Button svgIcon={searchIcon} />
                      )}
                    </span>
                  </>
                )}
              />
            </div>
          </div>
          <div>
            <Grid
              data={process(filteredData, dataState)}
              pageable={getPageableSettings()}
              skip={dataState.skip}
              take={dataState.take}
              total={filteredData.length}
              onPageChange={(e) => setDataState(e.page)}
              onDataStateChange={handlePageChange}
            >
              <GridColumn
                field="SNo"
                title="S.No"
                width={10}
                headerCell={RightAlignedHeaderCell}
                cell={(props) => (
                  <td
                    style={{
                      height: "75px",
                      textAlign: "right",
                      paddingRight: "40px",
                    }}
                  >
                    {props.dataIndex + 1}
                  </td>
                )}
              />
              <GridColumn
                field="B2BServiceName"
                title="B2B Service Name"
                filter="text"
                cell={(props) => (
                  <td
                    style={{
                      whiteSpace: "pre-wrap",
                      wordWrap: "break-word",
                    }}
                  >
                    {props.dataItem.B2BServiceName}
                  </td>
                )}
              />
              <GridColumn
                field="B2BServiceDescription"
                title="B2B Service Description"
                filter="text"
                cell={MultilineTextCell}
              />
              <GridColumn
                field="B2BServicePrice"
                title="B2B Service Price"
                className="customer-name-styles"
                headerCell={RightAlignedHeaderCell}
                cell={(props) => (
                  <td
                    style={{
                      height: "75px",
                      textAlign: "right",
                      paddingRight: "35px",
                    }}
                  >
                    {props.dataItem.B2BServicePrice
                      ? `${props.dataItem.B2BServicePrice}`
                      : ""}
                  </td>
                )}
              />
              <GridColumn
                title="Action"
                className="Data"
                width={"240px"}
                cell={(props) => (
                  <td>
                    <Button
                      className="edit-color"
                      disabled={props.dataItem.isPredefinedTax}
                      onClick={() => {
                        enterView(props.dataItem);
                      }}
                      svgIcon={pencilIcon}
                    />
                  </td>
                )}
              />
            </Grid>
          </div>
        </div>
      </div>
    </div>
  );
};
